import { LoaderState } from '@store/core/core.interface';
import { ConsentGrantRequest } from '@api/model/consentGrantRequest';
import { ConsentGrantResponse } from '@api/model/consentGrantResponse';
import { SupportedBy } from '@core/constants';
import { OrderPayment } from '@api/model/orderPayment';
import { ConsentStatus } from '@api/model/consentStatus';
import { PaymentConsentMerchant } from '@api/index';
import { IDVSTATUS } from '@core/constants/error-code';
import { ShippingAddress } from '@api/model/shippingAddress';
import { ShippingDeliveryOption } from '@api/model/shippingDeliveryOption';

export interface CheckoutFlowState {
  checkout: CheckoutState;
}
export interface ShippingOption {
  id: string;
  name: string;
  provider: string;
  description: string;
  price: number;
  isFree: boolean;
  title: string;
}

export enum ShippingOptionErrorCode {
  Timeout = 'Timeout',
  NoShippingOption = 'NoShippingOption',
  InvalidPostalCode = 'InvalidPostalCode',
  Others = 'Others'
}

export interface ShippingOptionError {
  code: ShippingOptionErrorCode;
  message: string;
  retry?: boolean;
}

export interface CheckoutState {
  consentId: string;
  consent?: LoaderState<Consent>;
  consentGrant?: ConsentGrant;
  paymentMethodsNotSupportedBy: SupportedBy;
  shippingCost?: LoaderState<OrderPayment>;
  merchantShippingOptions?: LoaderState<ShippingOption[]>;
  selectedShippingAddressLoading?: LoaderState<boolean>;
  idvStatus?: IDVSTATUS;
  selectedShippingOption?: ShippingOption;
  merchantAddresses: (ShippingAddress & {
    delivery_instructions?: string;
    default_shipping_address?: boolean;
    merchant_shipping_delivery_options?: ShippingDeliveryOption[];
  })[];
  selectedAddressRef: string | null;
  selectedPostalCode: string | null;
  shippingOptions: ShippingOption[];
  shippingOptionsError?: ShippingOptionError| null;
  loadingShippingOptions?: boolean;
  selectedShippingOptionId: string | null;
  checkoutFlowInitialized: boolean;
}

export interface ConsentGrant {
  payload?: Partial<ConsentGrantRequest>;
  error?: Error;
  consentGrantResponse?: Partial<ConsentGrantResponse>;
}
export interface Consent extends PaymentConsentMerchant {
  secured?: boolean;
  merchant_shipping_address?: (ShippingAddress & {
    delivery_instructions?: string;
    default_shipping_address?: boolean;
    merchant_shipping_delivery_options?: ShippingDeliveryOption[];
  })[];
}

export const MOCK_SHIPPING_OPTIONS: ShippingOption[] = [
  {
    id: 'free-shipping',
    name: 'CHECKOUT.SHIPPING_OPTIONS.FREE_SHIPPING',
    provider: 'CHECKOUT.SHIPPING_OPTIONS.PROVIDER_CANADA_POST',
    description: 'CHECKOUT.SHIPPING_OPTIONS.DELIVERY_3_5_DAYS',
    price: 0,
    isFree: true,
    title: 'CHECKOUT.SHIPPING_METHOD.LABEL',
  },
  {
    id: 'express',
    name: 'CHECKOUT.SHIPPING_OPTIONS.EXPRESS',
    provider: 'CHECKOUT.SHIPPING_OPTIONS.PROVIDER_CANADA_POST',
    description: 'CHECKOUT.SHIPPING_OPTIONS.DELIVERY_1_2_DAYS',
    price: 20.7,
    isFree: false,
    title: 'CHECKOUT.SHIPPING_METHOD.LABEL',
  },
  {
    id: 'pickup-store',
    name: 'CHECKOUT.SHIPPING_OPTIONS.PICKUP_IN_STORE',
    provider: 'CHECKOUT.SHIPPING_OPTIONS.PICKUP_ADDRESS',
    description: 'CHECKOUT.SHIPPING_OPTIONS.PICKUP_CITY',
    price: 0,
    isFree: true,
    title: 'CHECKOUT.SHIPPING_METHOD.LABEL',
  },
];

export const MOCK_MERCHANT_ADDRESSES = [
  {
    shipping_address_ref: 'merchant-addr-1',
    recipient_name: 'Jane doe',
    street_number: '1234',
    street_name: 'Maple Avenue',
    city: 'Toronto',
    postal_code: 'L7B 2A7',
    province: 'ON',
    country: 'CA' as ShippingAddress.CountryEnum,
    recipient_phone_number: '(416) 555-1234',
    delivery_instructions: 'This is a delivery instruction it will be displayed here when it is very long na...',
  },
  {
    shipping_address_ref: 'merchant-addr-2',
    recipient_name: 'Maxbun Lopes',
    street_number: '576',
    street_name: 'Josh Street',
    city: 'Toronto',
    postal_code: 'L7B 0A3',
    province: 'ON',
    country: 'CA' as ShippingAddress.CountryEnum,
    recipient_phone_number: '(416) 555-5678',
    delivery_instructions: 'Please leave package at the door. Ring doorbell twice.',
  },
  {
    shipping_address_ref: 'merchant-addr-3',
    recipient_name: 'Aaron Josh',
    street_number: '10',
    street_name: 'John Street',
    city: 'Toronto',
    postal_code: 'L72 2A2',
    province: 'ON',
    country: 'CA' as ShippingAddress.CountryEnum,
    recipient_phone_number: '(416) 555-9012',
  },
  {
    shipping_address_ref: 'merchant-addr-4',
    recipient_name: 'Jane Doe',
    street_number: '12',
    street_name: 'Adelaide',
    city: 'Toronto',
    postal_code: 'L7B 2A7',
    province: 'ON',
    country: 'CA' as ShippingAddress.CountryEnum,
    recipient_phone_number: '(416) 555-3456',
  },
];

export const initialCheckoutState: CheckoutState = {
  consentId: '',
  paymentMethodsNotSupportedBy: SupportedBy.FI,
  selectedShippingOption: undefined,
  selectedPostalCode: null,
  merchantShippingOptions: undefined,
  shippingOptions: [],
  selectedShippingOptionId: null,
  merchantAddresses: [],
  selectedAddressRef: null,
  checkoutFlowInitialized: false,
  loadingShippingOptions: false,
};

export interface PaymentAuthorized {
  merchantName: string;
  directToSuccess: boolean;
  currentConsentStatus: ConsentStatus;
}
